import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = [
    "bucket", "list", "handle",
    "confirmDialog", "confirmTitle", "confirmMessage", "confirmButton", "cancelButton", "confirmBackdrop"
  ]
  static values = { reorderUrl: String }

  connect() {
    this.bucketTargets.forEach((bucket) => {
      const list = bucket.querySelector('[data-intake-packet-sortable-target="list"]')
      if (!list) return

      Sortable.create(list, {
        group: "intake-packet",
        handle: '[data-intake-packet-sortable-target="handle"]',
        animation: 150,
        onEnd: (event) => this.handleDrop(event),
      })
    })
  }

  async handleDrop(event) {
    const dragged = event.item
    const fromBucket = event.from.closest("[data-bucket-key]")?.dataset.bucketKey
    const toBucket = event.to.closest("[data-bucket-key]")?.dataset.bucketKey
    const docId = dragged.dataset.documentId

    if (fromBucket !== toBucket) {
      const docName = dragged.querySelector(".text-sm.font-medium")?.textContent?.trim() || "this document"
      const label = toBucket.replace(/_/g, " ")
      const proceed = await this._confirm({
        title: "Change document audience?",
        message: `Change "${docName}" audience to ${label}?`,
        confirmText: "Change Audience"
      })
      if (!proceed) {
        // Revert: move item back to its original position in the source list
        if (event.oldIndex < event.from.children.length) {
          event.from.insertBefore(dragged, event.from.children[event.oldIndex])
        } else {
          event.from.appendChild(dragged)
        }
        return
      }
      this.persist({ [docId]: toBucket })
    } else {
      this.persist({})
    }
  }

  removeRow(event) {
    const row = event.currentTarget.closest("li[data-document-id]")
    if (!row) return
    row.remove()
    this.persist({})
  }

  // Promise-based modal confirm shared by handleDrop and removeRow. Falls
  // back to window.confirm only if the dialog markup is missing — the
  // dialog ships with the settings page so this is defensive.
  _confirm({ title, message, confirmText }) {
    if (!this.hasConfirmDialogTarget) {
      return Promise.resolve(window.confirm(message))
    }

    return new Promise((resolve) => {
      this.confirmTitleTarget.textContent = title
      this.confirmMessageTarget.textContent = message
      this.confirmButtonTarget.textContent = confirmText
      this.confirmDialogTarget.classList.remove("hidden")

      const close = (answer) => {
        this.confirmDialogTarget.classList.add("hidden")
        this.confirmButtonTarget.removeEventListener("click", onConfirm)
        this.cancelButtonTarget.removeEventListener("click", onCancel)
        if (this.hasConfirmBackdropTarget) {
          this.confirmBackdropTarget.removeEventListener("click", onCancel)
        }
        resolve(answer)
      }
      const onConfirm = () => close(true)
      const onCancel = () => close(false)

      this.confirmButtonTarget.addEventListener("click", onConfirm)
      this.cancelButtonTarget.addEventListener("click", onCancel)
      if (this.hasConfirmBackdropTarget) {
        this.confirmBackdropTarget.addEventListener("click", onCancel)
      }
    })
  }

  persist(audienceChanges) {
    const documentIds = []
    this.bucketTargets.forEach((bucket) => {
      bucket.querySelectorAll("li[data-document-id]").forEach((row) => {
        documentIds.push(row.dataset.documentId)
      })
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Capture the pre-reorder DOM so we can restore on failure — without
    // this, a network blip or 422 from a future server-side validation
    // would leave the UI in the new order while the server kept the old,
    // and the divergence is only visible after a refresh.
    const previousHtml = this.element.innerHTML

    fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ document_ids: documentIds, audience_changes: audienceChanges }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
      })
      .catch((error) => {
        console.warn("[intake-packet-sortable] reorder failed:", error)
        this.element.innerHTML = previousHtml
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: { type: "error", message: "Could not save changes. Refresh and try again." }
        }))
      })
  }
}
